<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";
import { t } from "@/plugins/i18n";
import { collapseCrumbs } from "./hierarchyTree";
import { buildLevelRows, type HierarchyRow } from "./hierarchyRows";
import FilterList from "./FilterList.vue";
import HierarchyStructure from "./HierarchyStructure.vue";
import { soleSelectedKey, type ValueRow } from "./valueRow";
import type { Branch } from "./useExcludedBranch";
import type { useHierarchyTree } from "./useHierarchyTree";

const { tree, isLoading, errorMessage } = defineProps<{
	tree: ReturnType<typeof useHierarchyTree>;
	isLoading?: boolean;
	/** Replaces the empty-list copy when the hierarchy failed to load at all. */
	errorMessage?: string | null;
}>();

const isStructureOpen = ref(false);
const isExclusionsOpen = ref(false);

// Emptying the list from inside the menu would let its own `v-if` tear the open
// overlay down mid-patch, so close it first and mutate on the next flush.
const removeExclusion = async (branch: Branch) => {
	if (tree.exclusions.value.length > 1) {
		tree.removeExclusion(branch);
		return;
	}

	isExclusionsOpen.value = false;
	await nextTick();
	tree.removeExclusion(branch);
};

const clearExclusions = async () => {
	isExclusionsOpen.value = false;
	await nextTick();
	tree.clearExclusions();
};

const rows = computed(() =>
	buildLevelRows(tree.root.value, tree.scopeBranch.value, tree.depth.value, {
		isPruned: tree.isBranchPruned,
	})
);

const branchesOf = (row: ValueRow): Branch[] => (row as HierarchyRow).branches;

const isChecked = (row: ValueRow) =>
	!branchesOf(row).some((branch) => tree.isBranchPruned(branch));

const isIndeterminate = (row: ValueRow) =>
	branchesOf(row).some((branch) => tree.isBranchIndeterminate(branch));

const canToggle = (row: ValueRow) =>
	branchesOf(row).every((branch) => tree.canExclude(branch));

const onToggle = (row: ValueRow) => {
	const exclude = isChecked(row);

	branchesOf(row).forEach((branch) => tree.setExcluded(branch, exclude));
};

const onOnly = (row: ValueRow, scope: ValueRow[]) => {
	const branches = scope.flatMap(branchesOf);

	if (soleSelectedKey(scope, isChecked) === row.key) {
		tree.includeAll(branches);
		return;
	}

	tree.isolate(branchesOf(row), branches);
};

const setAll = (rows: ValueRow[], excluded: boolean) =>
	rows.forEach((row) =>
		branchesOf(row).forEach((branch) => tree.setExcluded(branch, excluded))
	);

// `canDrill` is structural, so an excluded row still reports as drillable.
// Descending into one means nothing, so the click puts the row back instead.
const onDrill = (row: ValueRow) => {
	if (!isChecked(row)) {
		onToggle(row);
		return;
	}

	tree.setScope(branchesOf(row)[0]);
};

const onContextMenu = (mouseEvent: MouseEvent) => {
	const target = mouseEvent.target as HTMLElement | null;

	if (target?.closest("input, textarea, [contenteditable='true']")) {
		return;
	}

	mouseEvent.preventDefault();
	tree.goUp();
};

watch(isStructureOpen, (open) => {
	if (open) {
		tree.expandPath();
	}
});

const crumbs = computed(() => collapseCrumbs(tree.breadcrumb.value));

const withCount = (key: string, plural: string, count: number) =>
	t(count === 1 ? key : plural, { count });

const depthLabel = (count: number) =>
	withCount("filterDialog.unit.level", "filterDialog.unit.levels", count);

const yieldLabel = (count: number) =>
	withCount(
		"filterDialog.unit.category",
		"filterDialog.unit.categories",
		count
	);

const exclusionLabel = computed(() =>
	t("filterDialog.hierarchy.excludedCount", {
		count: tree.exclusions.value.length,
	})
);
</script>
<template>
	<section class="frame" :class="{ 'frame--bare': errorMessage }">
		<header v-if="!errorMessage" class="head">
			<v-menu
				v-if="tree.depthOptions.value.length > 0"
				transition="slide-y-transition"
			>
				<template #activator="{ props: menu }">
					<v-btn
						v-bind="menu"
						color="info"
						variant="tonal"
						size="small"
						height="26"
						prepend-icon="fa: fa-regular fa-arrow-down-short-wide"
						v-tooltip="t('filterDialog.hierarchy.breakDownBy')"
					>
						{{ depthLabel(tree.depth.value) }}
					</v-btn>
				</template>
				<v-list density="compact">
					<v-list-item
						v-for="option in tree.depthOptions.value"
						:key="option.depth"
						:active="option.depth === tree.depth.value"
						@click="tree.setDepth(option.depth)"
					>
						<v-list-item-title>
							{{ depthLabel(option.depth) }}
						</v-list-item-title>
						<v-list-item-subtitle>
							{{ yieldLabel(option.count) }}
							<template v-if="option.examples.length">
								— {{ option.examples.join(", ") }}
							</template>
						</v-list-item-subtitle>
						<template v-if="option.sameAsPrevious" #append>
							<span
								v-tooltip="
									t('filterDialog.hierarchy.sameAsPrevious', {
										depth: depthLabel(option.depth - 1),
									})
								"
								class="hint"
							>
								<v-icon
									icon="fa: fa-regular fa-triangle-exclamation"
									size="12"
									color="warning"
								/>
							</span>
						</template>
					</v-list-item>
				</v-list>
			</v-menu>
			<v-menu
				v-model="isStructureOpen"
				:close-on-content-click="false"
				transition="slide-y-transition"
			>
				<template #activator="{ props: menu }">
					<v-btn
						v-bind="menu"
						:active="isStructureOpen"
						color="on-background"
						variant="tonal"
						size="small"
						height="26"
						prepend-icon="fa: fa-regular fa-list-tree"
						v-tooltip="t('filterDialog.hierarchy.structureHint')"
					>
						{{ t("filterDialog.hierarchy.structure") }}
					</v-btn>
				</template>
				<div class="structure">
					<hierarchy-structure
						v-if="tree.root.value"
						:tree="tree"
						:node="tree.root.value"
						:branch="[]"
						:close="() => (isStructureOpen = false)"
					/>
				</div>
			</v-menu>
			<nav class="path">
				<template v-if="crumbs.lead">
					<button
						type="button"
						class="crumb"
						@click="tree.setScope(crumbs.lead.branch)"
					>
						{{ crumbs.lead.visibleLabel }}
					</button>
					<span class="separator">›</span>
					<v-menu transition="slide-y-transition">
						<template #activator="{ props: menu }">
							<button type="button" class="crumb" v-bind="menu">…</button>
						</template>
						<v-list density="compact">
							<v-list-item
								v-for="crumb in crumbs.hidden"
								:key="crumb.branch.join('.')"
								@click="tree.setScope(crumb.branch)"
							>
								<v-list-item-title>{{ crumb.visibleLabel }}</v-list-item-title>
							</v-list-item>
						</v-list>
					</v-menu>
					<span class="separator">›</span>
				</template>
				<template
					v-for="(crumb, index) in crumbs.tail"
					:key="crumb.branch.join('.')"
				>
					<span v-if="index > 0" class="separator">›</span>
					<button
						v-if="index < crumbs.tail.length - 1"
						type="button"
						class="crumb"
						@click="tree.setScope(crumb.branch)"
					>
						{{ crumb.visibleLabel }}
					</button>
					<span v-else class="crumb current">{{ crumb.visibleLabel }}</span>
				</template>
			</nav>
			<v-spacer />
			<v-menu
				v-if="tree.exclusions.value.length > 0"
				v-model="isExclusionsOpen"
			>
				<template #activator="{ props: menu }">
					<v-chip color="error" size="small" v-bind="menu" class="clickable">
						{{ exclusionLabel }}
					</v-chip>
				</template>
				<v-list density="compact">
					<v-list-item
						v-for="exclusion in tree.exclusions.value"
						:key="exclusion.branch.join('.')"
					>
						<v-list-item-title>{{ exclusion.label }}</v-list-item-title>
						<v-list-item-subtitle v-if="exclusion.parents.length">
							{{ exclusion.parents.join(" › ") }}
						</v-list-item-subtitle>
						<template #append>
							<v-btn
								variant="text"
								size="x-small"
								icon="fa: fa-regular fa-xmark"
								v-tooltip="t('filterDialog.hierarchy.removeExclusion')"
								@click="removeExclusion(exclusion.branch)"
							/>
						</template>
					</v-list-item>
					<v-divider />
					<v-list-item @click="clearExclusions()">
						<v-list-item-title>
							{{ t("filterDialog.hierarchy.clearExclusions") }}
						</v-list-item-title>
					</v-list-item>
				</v-list>
			</v-menu>
		</header>
		<v-progress-linear v-if="isLoading" indeterminate height="2" />
		<div @contextmenu="onContextMenu">
			<filter-list
				flush
				:rows="rows"
				:is-checked="isChecked"
				:is-indeterminate="isIndeterminate"
				:can-toggle="canToggle"
				:allow-bulk="false"
				:search-placeholder="t('filterDialog.hierarchy.searchLevel')"
				:empty-message="errorMessage ?? t('filterDialog.hierarchy.empty')"
				@toggle="onToggle"
				@only="onOnly"
				@drill="onDrill"
				@select-all="(selected) => setAll(selected, false)"
				@deselect-all="(selected) => setAll(selected, true)"
			/>
		</div>
	</section>
</template>
<style scoped lang="scss">
.frame {
	border: 1px solid rgb(var(--color-secondary));
	border-radius: 4px;
	overflow: hidden;
}

// Nothing loaded, so there is no tree to frame — the message stands on its own.
.frame--bare {
	border: none;
	border-radius: 0;
}

.head {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	padding: 0.375rem 0.75rem;
	border-bottom: 1px solid rgb(var(--color-secondary));
	background: rgb(var(--color-surface));
}

.hint {
	display: inline-flex;
	align-items: center;
	cursor: help;
}

.clickable {
	cursor: pointer;
}

.head :deep(.v-btn) {
	text-transform: none;
	letter-spacing: 0;
}

.path {
	display: flex;
	align-items: center;
	min-width: 0;
	overflow: hidden;
	white-space: nowrap;
}

.crumb {
	flex: 0 1 auto;
	min-width: 0;
	padding-inline: 0.25rem;
	overflow: hidden;
	text-overflow: ellipsis;
	font-size: 0.8125rem;
	color: rgb(var(--color-on-background));
	text-decoration: underline;
	cursor: pointer;
}

.crumb.current {
	font-weight: 600;
	color: rgb(var(--color-on-surface));
	text-decoration: none;
	cursor: default;
}

.separator {
	flex: 0 0 auto;
	padding-inline: 0.125rem;
	font-size: 0.75rem;
	color: rgb(var(--color-on-background));
}

.structure {
	min-width: 22rem;
	max-width: 30rem;
	max-height: 60vh;
	overflow: auto;
	padding: 0.5rem;
	background: rgb(var(--color-surface));
}
</style>
